/**
 * Created by JIA on 2023/10/10.
 */
import { Segment, useDefault, cnPOSTag, POSTAG } from "segmentit";
import WinkTokenizer from "wink-tokenizer";
import { ToWords } from "to-words";
import nzhcn from "nzh/cn";
import _ from "lodash";
import { JoinWords, ConvertParsedWordToString, EnumHasFlag, GetPlainTextFromSentence, ParseSentence } from "./MarkTextHelper";
import Constants from "../config/Constants";

/**
 * makeOrdinal start
 */
const ENDS_WITH_DOUBLE_ZERO_PATTERN = /(hundred|thousand|(m|b|tr|quadr)illion)$/;
const ENDS_WITH_TEEN_PATTERN = /teen$/;
const ENDS_WITH_Y_PATTERN = /y$/;
const ENDS_WITH_ZERO_THROUGH_TWELVE_PATTERN = /(zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)$/;
const ordinalLessThanThirteen = {
    zero: "zeroth",
    one: "first",
    two: "second",
    three: "third",
    four: "fourth",
    five: "fifth",
    six: "sixth",
    seven: "seventh",
    eight: "eighth",
    nine: "ninth",
    ten: "tenth",
    eleven: "eleventh",
    twelve: "twelfth"
};

/**
 * Converts a number-word into an ordinal number-word.
 * @example makeOrdinal('one') => 'first'
 * @param {string} words
 * @returns {string}
 */
function makeOrdinal(words) {
    // Ends with *00 (100, 1000, etc.) or *teen (13, 14, 15, 16, 17, 18, 19)
    if (ENDS_WITH_DOUBLE_ZERO_PATTERN.test(words) || ENDS_WITH_TEEN_PATTERN.test(words)) {
        return words + 'th';
    }
    // Ends with *y (20, 30, 40, 50, 60, 70, 80, 90)
    else if (ENDS_WITH_Y_PATTERN.test(words)) {
        return words.replace(ENDS_WITH_Y_PATTERN, 'ieth');
    }
    // Ends with one through twelve
    else if (ENDS_WITH_ZERO_THROUGH_TWELVE_PATTERN.test(words)) {
        return words.replace(ENDS_WITH_ZERO_THROUGH_TWELVE_PATTERN, replaceWithOrdinalVariant);
    }
    return words;
}

function replaceWithOrdinalVariant(match, numberWord) {
    return ordinalLessThanThirteen[numberWord];
}


//End

const toWords = new ToWords({
    localeCode: "en-US",
    converterOptions: {
        currency: false,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false,
    },
});

/**
 * 按单个数字转换
 *  1995 -> 一九九五
 * @param str
 * @returns {*}
 * @constructor
 */
const InternalConvertNumberToChineseNumByChar = (str) => {
    const targetStr = _.toString(str);
    return _.join(
        _.map(targetStr.split(""), (c) => nzhcn.encodeS(_.toString(c))),
        ""
    );
};

const ConvertNumberToChineseNumByChar = (str) => {
    //extract number in str
    const splitArr = splitByNum(_.toString(str));
    let result = "";
    for (let i = 0; i < splitArr.length; i++) {
        const piece = splitArr[i];
        if (isNumber(piece)) {
            result += `\\n{${piece}|${InternalConvertNumberToChineseNumByChar(piece)}}`;
        } else {
            result += piece;
        }
    }
    return result;
};
const InternalConvertNumberToEnglishNumByChar = (str) => {
    const targetStr = _.toString(str);
    return _.join(
        _.map(targetStr.split(""), (c) => toWords.convert(_.toNumber(c))),
        ""
    );
};

const ConvertNumberToEnglishNumByChar = (str) => {
    //extract number in str
    const splitArr = splitByNum(_.toString(str));
    let result = "";
    for (let i = 0; i < splitArr.length; i++) {
        const piece = splitArr[i];
        if (isNumber(piece)) {
            result += `\\n{${piece}|${InternalConvertNumberToEnglishNumByChar(piece)}}`;
        } else {
            result += piece;
        }
    }
    return result;
};

/**
 * 连续数字的转换
 *  1995 -> 一千九百九十五
 * @param str
 * @returns {*}
 * @constructor
 */
const ConvertNumberToChineseNumByStr = (str) => {
    //extract number in str
    const atozRegex = /(\d+)[\-\－\~\～](\d+)/g;
    let result = "";
    if (atozRegex.test(str)) {
        //1-6
        let arr;
        if (str.indexOf("-") > -1) {
            arr = str.split("-");
        } else if (str.indexOf("－") > -1) {
            arr = str.split("－");
        } else if (str.indexOf("~") > -1) {
            arr = str.split("~");
        } else if (str.indexOf("～") > -1) {
            arr = str.split("～");
        }
        result += `\\n{${str}|${nzhcn.encodeS(_.toNumber(arr[0]))}到${nzhcn.encodeS(_.toNumber(arr[1]))}}`;
    } else {
        const splitArr = splitByNum(_.toString(str));

        for (let i = 0; i < splitArr.length; i++) {
            const piece = splitArr[i];
            //handle 10% -> 百分之10 -> 百分之十
            if (_.endsWith(piece, "%")) {
                let numPartStr = piece.substring(0, piece.length - 1);
                result += `\\n{${piece}|百分之${nzhcn.encodeS(_.toNumber(numPartStr))}}`;
            } else if (isNumber(piece)) {
                result += `\\n{${piece}|${nzhcn.encodeS(_.toNumber(piece))}}`;
            } else {
                result += piece;
            }
        }
    }

    return result;
};

/**
 * 连续数字的转换
 *  1995 -> 一千九百九十五
 * @param str
 * @returns {*}
 * @constructor
 */
const ConvertNumberToEnglishNumByStr = (str) => {
    //extract number in str
    const atozRegex = /(\d+)[\-\－\~\～](\d+)/g;
    let result = "";
    if (atozRegex.test(str)) {
        //1-6
        let arr;
        if (str.indexOf("-") > -1) {
            arr = str.split("-");
        } else if (str.indexOf("－") > -1) {
            arr = str.split("－");
        } else if (str.indexOf("~") > -1) {
            arr = str.split("~");
        } else if (str.indexOf("～") > -1) {
            arr = str.split("～");
        }
        result += `\\n{${str}|${toWords.convert(_.toNumber(arr[0]))} to ${toWords.convert(_.toNumber(arr[1]))}}`;
    } else {
        const splitArr = splitByNum(_.toString(str));

        const finalArr = [];
        for (let i = 0; i < splitArr.length; i++) {
            const piece = splitArr[i];
            //handle 10% -> 百分之10 -> 百分之十
            if (_.endsWith(piece, "%")) {
                let numPartStr = piece.substring(0, piece.length - 1);
                finalArr.push(`\\n{${piece}|${toWords.convert(_.toNumber(numPartStr))} Percent}`);
            } else if (isNumber(piece)) {
                finalArr.push(`\\n{${piece}|${toWords.convert(_.toNumber(piece))}}`);
            } else {
                finalArr.push(piece);
            }
        }

        result = _.join(finalArr, " ");
    }

    return result;
};

/**
 * 1st => first
 * @param str
 * @returns {string}
 * @constructor
 */
const ConvertNumberToEnglishNumByOrdinal = (str) => {
    //extract number in str
    let result = str;
    try {
        //console.log(parseInt("1st")) => 1
        result = `\\n{${str}|${makeOrdinal(toWords.convert(parseInt(str)))}}`;
    } catch (e) {}

    return result;
};

const ConvertCurrencySymbolToLocale = (currencySymbol) => {
    const currency_symbols = {
        $: "en-US", // US Dollar
        "€": "en-IE", // Euro
        "£": "en-GB", // British Pound Sterling
        "₹": "en-IN", // Indian Rupee
        //"¥": "yen", // Japanese Yen
    };

    if (_.has(currency_symbols, currencySymbol)) {
        return currency_symbols[currencySymbol];
    }
    return "usd";
};

/**
 * $1 => 1 dollar
 * @param currencySymbol
 * @param str
 * @returns {string}
 * @constructor
 */
const ConvertNumberToEnglishNumByCurrency = (currencySymbol, str) => {
    //extract number in str
    let result = str;
    if (isNumber(str)) {
        try {
            const locale = ConvertCurrencySymbolToLocale(currencySymbol);
            const toWordsCurrency = new ToWords({
                localeCode: locale,
                converterOptions: {
                    currency: true,
                    ignoreDecimal: false,
                    ignoreZeroCurrency: true,
                    doNotAddOnly: true,
                },
            });

            result = `\\n{${currencySymbol}${str}|${toWordsCurrency.convert(_.toNumber(str))}}`;
        } catch (e) {}
    }

    return result;
};

/**
 * Should be only 610.9块 , 310个, 110名,
 *  so we just split at last
 * @param str
 * @returns {*[]}
 */
const splitByNum = (str) => {
    const validRegex = /[\d\-\－\,\.\+\%]/;
    const result = [];
    let tempResult = "";
    let previousNum = false;
    for (let i = 0; i < str.length; i++) {
        const item = str[i];
        if (validRegex.test(item)) {
            if (!previousNum && i > 0) {
                result.push(tempResult);
                tempResult = "";
            }
            tempResult += item;
            previousNum = true;
        } else {
            if (previousNum) {
                result.push(tempResult);
                tempResult = "";
            }
            tempResult += item;
            previousNum = false;
        }
    }
    if (tempResult.length > 0) {
        result.push(tempResult);
    }
    return result;
};

const hasNumber = (myString) => {
    return /\d/.test(myString);
};
const isNumber = (str) => {
    if (typeof str != "string") return false; // we only process strings!
    return (
        !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
        !isNaN(parseFloat(str))
    ); // ...and ensure strings of whitespace fail
};

const AutoMarkNumToChineseNumForPlainText = (str) => {
    //no number return, for performance
    if (!hasNumber(str)) {
        return str;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const segmentit = useDefault(new Segment());
    const segment = segmentit.doSegment(str);
    /*
    *
var CN_POS_NAMES = {
  D_A: '形容词 形语素',
  D_B: '区别词 区别语素',
  D_C: '连词 连语素',
  D_D: '副词 副语素',
  D_E: '叹词 叹语素',
  D_F: '方位词 方位语素',
  D_I: '成语',
  D_L: '习语',
  A_M: '数词 数语素',
  D_MQ: '数量词',
  D_N: '名词 名语素',
  D_O: '拟声词',
  D_P: '介词',
  A_Q: '量词 量语素',
  D_R: '代词 代语素',
  D_S: '处所词',
  D_T: '时间词',
  D_U: '助词 助语素',
  D_V: '动词 动语素',
  D_W: '标点符号',
  D_X: '非语素字',
  D_Y: '语气词 语气语素',
  D_Z: '状态词',
  A_NR: '人名',
  A_NS: '地名',
  A_NT: '机构团体',
  A_NX: '外文字符',
  A_NZ: '其他专名',
  D_ZH: '前接成分',
  D_K: '后接成分',
  UNK: '未知',
  URL: '网址 邮箱地址'
};
    * */

    //考虑  A_M: '数词 数语素',
    //   D_MQ: '数量词',
    //  A_Q: '量词 量语素',
    // D_T: '时间词',
    let result = [];
    const isAtoZCase = (z, i, azSet) => {
        if (z.w === "-" || z.w === "－" || z.w === "~" || z.w === "～") {
            if (i > 0 && i + 1 < segment.length) {
                if (azSet.indexOf(i - 1) > -1) {
                    return null;
                } else if (azSet.indexOf(i) > -1) {
                    return null;
                } else if (azSet.indexOf(i + 1) > -1) {
                    return null;
                }

                let lastW = segment[i - 1].w;
                let nextW = segment[i + 1].w;
                if (isNumber(lastW) && isNumber(nextW)) {
                    return {
                        lastW: lastW,
                        w: z.w,
                        nextW: nextW,
                        wholeStr: lastW + z.w + nextW,
                    };
                }
            }
        }
        return null;
    };
    const azSet = [];
    for (let i = 0; i < segment.length; i++) {
        const item = segment[i];
        const targetP = _.isArray(item.p) ? (item.p.length > 0 ? item.p[0] : "") : item.p;

        let aZConfig = isAtoZCase(item, i, azSet);
        if (aZConfig) {
            //delete last
            result.splice(result.length - 1, 1);
            result.push(ConvertNumberToChineseNumByStr(aZConfig.wholeStr));
            azSet.push(i - 1);
            azSet.push(i);
            azSet.push(i + 1);
            i += 1; //skip next

            continue;
        }
        if (hasNumber(item.w)) {
            //p maybe a array
            switch (targetP) {
                case POSTAG.A_M: //6: "1009 <数词 数语素> <m>" "10% <数词 数语素> <m>"
                    result.push(ConvertNumberToChineseNumByStr(item.w));
                    break;
                case POSTAG.D_MQ: //"610.9块 <数量词> <mq>"
                    result.push(ConvertNumberToChineseNumByStr(item.w));
                    break;
                case POSTAG.D_T: //"1997年 <时间词> <t>"  "1997年3月3日 <时间词> <t>"
                    result.push(ConvertNumberToChineseNumByChar(item.w));
                    break;
                default:
                    result.push(item.w);
                    break;
            }
        } else {
            result.push(item.w);
        }
    }

    return JoinWords(result);
};

const AutoMarkNumToEnglishNumForPlainText = (str) => {
    //no number return, for performance
    if (!hasNumber(str)) {
        return str;
    }

    const winkTokenizer = WinkTokenizer();
    winkTokenizer.addRegex(/\d*(\.*)\d+%/gi, "percentage", "per");
    //winkTokenizer.addRegex(/hello/gi, "negNumber", "negativeNumber");
    const segment = winkTokenizer.tokenize(str);

    //考虑  currency: such as $ or £ symbols (r),
    //   number: any integer, decimal number, fractions such as 19, 2.718 or 1/4 and numerals containing ", - / .", for example 12-12-1924 (n),
    //  ordinal: 'ordinals like 1st, 2nd, 3rd, 4th or 12th or 91st (o)',
    // time: 'common representation of time such as 4pm or 16:00 hours (t)',
    let result = [];
    const isAtoZCase = (z, i, azSet) => {
        if (z.value === "-" || z.value === "－" || z.value === "~" || z.value === "～") {
            if (i > 0 && i + 1 < segment.length) {
                if (azSet.indexOf(i - 1) > -1) {
                    return null;
                } else if (azSet.indexOf(i) > -1) {
                    return null;
                } else if (azSet.indexOf(i + 1) > -1) {
                    return null;
                }

                let lastW = segment[i - 1].value;
                let nextW = segment[i + 1].value;
                if (isNumber(lastW) && isNumber(nextW)) {
                    return {
                        lastW: lastW,
                        w: z.value,
                        nextW: nextW,
                        wholeStr: lastW + z.value + nextW,
                    };
                }
            }
        }
        return null;
    };
    console.log(segment)
    const azSet = [];
    for (let i = 0; i < segment.length; i++) {
        const item = segment[i];
        const targetP = _.isArray(item.tag) ? (item.tag.length > 0 ? item.tag[0] : "") : item.tag;

        let aZConfig = isAtoZCase(item, i, azSet);
        if (aZConfig) {
            //delete last
            result.splice(result.length - 1, 1);
            result.push(ConvertNumberToEnglishNumByStr(aZConfig.wholeStr));
            azSet.push(i - 1);
            azSet.push(i);
            azSet.push(i + 1);
            i += 1; //skip next

            continue;
        }
        if (hasNumber(item.value)) {
            //p maybe a array
            switch (targetP) {
                case "ordinal": //ordinals like 1st, 2nd, 3rd, 4th or 12th or 91st (o)
                    result.push(ConvertNumberToEnglishNumByOrdinal(item.value));
                    break;
                case "number": //any integer, decimal number, fractions such as 19, 2.718 or 1/4 and numerals containing ", - / .", for example 12-12-1924 (n)
                    result.push(ConvertNumberToEnglishNumByStr(item.value));
                    break;
                case "percentage": //1000%
                    result.push(ConvertNumberToEnglishNumByStr(item.value));
                    break;
                case "time": //common representation of time such as 4pm or 16:00 hours (t)
                    result.push(ConvertNumberToEnglishNumByStr(item.value));
                    break;
                default:
                    result.push(item.value);
                    break;
            }
        } else {
            switch (targetP) {
                case "currency": //such as $ or £ symbols (r)
                    {
                        let notPushed = true;
                        if (i < segment.length) {
                            const nextW = segment[i + 1];

                            if (nextW.tag === "number") {
                                result.push(ConvertNumberToEnglishNumByCurrency(item.value, nextW.value));
                                notPushed = false;
                                i += 1;
                            }
                        }

                        if (notPushed) {
                            result.push(item.value);
                        }
                    }
                    break;
                case "punctuation": //such as $ or £ symbols (r)
                    {
                        let notPushed = true;
                        if (item.value === "-" && i < segment.length) {
                            const nextW = segment[i + 1];
                            if (nextW.tag === "number") {
                                result.push(ConvertNumberToEnglishNumByStr(`${item.value}${nextW.value}`));
                                notPushed = false;
                                i += 1;
                            }
                        }

                        if (notPushed) {
                            result.push(item.value);
                        }
                    }
                    break;
                default:
                    result.push(item.value);
                    break;
            }
        }
    }

    return JoinWords(result, true);
};

/**
 * 自动为中文里面的阿拉伯数字标注中文读音
 * @param str
 * @constructor
 */
const AutoMarkNumToChineseNum = (str) => {
    //no number return, for performance
    if (!hasNumber(str)) {
        return str;
    }
    let result = "";

    let newWords = ParseSentence(str).words || [];
    for (let k = 0; k < newWords.length; k++) {
        let word = newWords[k];
        if (word.type === Constants.TEXT_MARK_TYPE.PLAIN_TEXT) {
            result += AutoMarkNumToChineseNumForPlainText(word.text);
        } else {
            //ignore if you have mark
            result += ConvertParsedWordToString(word);
        }
    }

    return result;
};

/**
 * 自动为英文里面的阿拉伯数字标注英文读音
 * @param str
 * @constructor
 */
const AutoMarkNumToEnglishNum = (str) => {
    //no number return, for performance
    if (!hasNumber(str)) {
        return str;
    }
    let result = "";

    let newWords = ParseSentence(str).words || [];
    for (let k = 0; k < newWords.length; k++) {
        let word = newWords[k];
        if (word.type === Constants.TEXT_MARK_TYPE.PLAIN_TEXT) {
            result += AutoMarkNumToEnglishNumForPlainText(word.text);
        } else {
            //ignore if you have mark
            result += ConvertParsedWordToString(word);
        }
    }

    return result;
};

const AutoMarkNum = (lang, str) => {
    switch (lang) {
        case "zh":
        default:
            return AutoMarkNumToChineseNum(str);
        case "en":
            console.log(str, AutoMarkNumToEnglishNum(str));
            return AutoMarkNumToEnglishNum(str);
    }
};

export { AutoMarkNumToChineseNum, AutoMarkNumToEnglishNum, AutoMarkNum };
